import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"
import { comoFecha } from "@/lib/fechas"

import { comoSeLlama, type Empresa, urlLogo } from "./tipos"

/**
 * El membrete de la agencia. **El único sitio que lo compone.**
 *
 * ⚠ **Existe para que no haya dos membretes.** En el 2.0 el encabezado se arma en
 * `renderMarcaEmpresa()` y eso funciona; lo que no funciona es tener el logo y el RIF
 * escritos también en cada plantilla, que es como acaban los sistemas donde el
 * encabezado se copia. Aquí lo usan el manual, los documentos del SGI y la vista
 * previa de la configuración, y cambiar el logo los cambia los tres.
 *
 * ⚠ **El logo oscuro es opcional y su ausencia no deja hueco.** Sin él se pinta el
 * claro con un filtro de tinta blanca. Sin eso, media plantilla vería un logo negro
 * sobre fondo negro mientras alguien prepara la otra versión — y al imprimir, que
 * siempre es sobre blanco, se usa el claro pase lo que pase.
 *
 * ⚠ **Si no hay logo, el membrete NO se rompe:** se queda el texto. Un documento
 * controlado sin logo sigue siendo válido; uno con un icono de imagen rota, no.
 */

export function EncabezadoEmpresa({
  titulo,
  fecha,
  empresa,
  sinEtiquetaSistema = false,
}: {
  /** Lo que va a la derecha: «Manual de Organización», «SETIECA-SGI-PRO-003»… */
  titulo?: string
  fecha?: string
  /** Si no se pasa, lo pide él. Se pasa cuando ya se tiene, para no pedirlo dos veces. */
  empresa?: Empresa
  /**
   * Sin la línea del sistema («SetiecaDesk 3.0»). La lleva lo que la agencia emite
   * desde su sistema; un comprobante de retención es un papel del CLIENTE hacia un
   * tercero, y esa firma del software no le corresponde (lo pidió el usuario).
   */
  sinEtiquetaSistema?: boolean
}) {
  const consulta = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
    enabled: empresa === undefined,
  })

  const datos = empresa ?? consulta.data
  if (!datos) return null

  // La versión de la caché: mientras no se recargue, la misma URL. Al guardar, la
  // consulta se invalida y con ella cambia el objeto — basta con algo que dependa
  // de los dos campos de logo.
  const version = Number(datos.tiene_logo) * 2 + Number(datos.tiene_logo_oscuro)

  return (
    <header
      className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b-2 pb-3"
      style={{ borderColor: "var(--primary-texto)" }}
    >
      <div className="flex min-w-0 items-center gap-3">
        {datos.tiene_logo && (
          <>
            {/* Dos etiquetas y no una con filtro condicional: el tema lo decide el
                CSS, no React, y así imprimir siempre saca la clara. */}
            <img
              src={urlLogo("claro", version)}
              alt=""
              className="solo-claro max-h-16 max-w-[180px] object-contain"
            />
            <img
              src={urlLogo(datos.tiene_logo_oscuro ? "oscuro" : "claro", version)}
              alt=""
              className={`solo-oscuro max-h-16 max-w-[180px] object-contain ${
                datos.tiene_logo_oscuro ? "" : "tinta-blanca"
              }`}
            />
          </>
        )}
        <div className="min-w-0">
          <p className="text-base font-bold leading-tight">{comoSeLlama(datos)}</p>
          {datos.rif && (
            <p className="text-xs tabular" style={{ color: "var(--fg-muted)" }}>
              RIF {datos.rif}
            </p>
          )}
          {datos.direccion && (
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {datos.direccion}
            </p>
          )}
          {datos.etiqueta_sistema && !sinEtiquetaSistema && (
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {datos.etiqueta_sistema}
            </p>
          )}
        </div>
      </div>

      {(titulo || fecha) && (
        <div className="text-right">
          {titulo && (
            <p
              className="text-sm font-bold uppercase tracking-widest"
              style={{ color: "var(--primary-texto)" }}
            >
              {titulo}
            </p>
          )}
          {fecha && (
            <p className="text-xs tabular" style={{ color: "var(--fg-muted)" }}>
              {comoFecha(fecha)}
            </p>
          )}
        </div>
      )}
    </header>
  )
}
